/**
 * Beschriftungen der Rechtsformen und ihrer Organrollen.
 *
 * Eigener Schlüsselraum `LegalForm.*`: Die Bezeichnungen sind Rechtsbegriffe und dürfen nicht mit
 * allgemeinen Formularbegriffen kollidieren. Die deutschen Werte stammen aus der m1member-Fassung,
 * die diese Rollen bereits in Briefen und Systemdokumenten führt.
 */

export const sharedLegalFormDe = {
  "LegalForm.Label": "Rechtsform",
  "LegalForm.RegisterCourt": "Registergericht",
  "LegalForm.RegisterNumber": "Registernummer",
  "LegalForm.RegisterNumberPlaceholder": "z. B. HRB 12345",
  "LegalForm.SupervisoryBoardChair": "Vorsitz des Aufsichtsrats",
  "LegalForm.SoleProprietorship": "Einzelunternehmen",
  "LegalForm.Gbr": "GbR",
  "LegalForm.Egbr": "eGbR",
  "LegalForm.RegisteredMerchant": "eingetragener Kaufmann / eingetragene Kauffrau",
  "LegalForm.Gmbh": "GmbH",
  "LegalForm.Ug": "UG (haftungsbeschränkt)",
  "LegalForm.Ag": "AG",
  "LegalForm.Ohg": "OHG",
  "LegalForm.Kg": "KG",
  "LegalForm.GmbhCoKg": "GmbH & Co. KG",
  "LegalForm.Ggmbh": "gGmbH",
  "LegalForm.Se": "SE",
  "LegalForm.Ev": "eingetragener Verein (e.V.)",
  "LegalForm.RepresentativesOwner": "Inhaber",
  "LegalForm.RepresentativesPartners": "Gesellschafter",
  "LegalForm.RepresentativesManagingDirectors": "Geschäftsführer",
  "LegalForm.RepresentativesManagementBoard": "Vorstand",
  "LegalForm.RepresentativesGeneralPartners": "persönlich haftende Gesellschafter",
  "LegalForm.RepresentativesGeneralPartnerCompany": "persönlich haftende Gesellschafterin",
  "LegalForm.CompanyNameHint": "vollständige Firma mit Rechtsformzusatz",
  "LegalForm.NameMismatch": "Der Zusatz im Firmennamen passt nicht zur gewählten Rechtsform.",
  "LegalForm.UgSuffixIncomplete": "Eine UG muss den Zusatz „(haftungsbeschränkt)“ im Firmennamen führen.",
} as const;

export const sharedLegalFormEn = {
  "LegalForm.Label": "Legal form",
  "LegalForm.RegisterCourt": "Register court",
  "LegalForm.RegisterNumber": "Register number",
  "LegalForm.RegisterNumberPlaceholder": "e.g. HRB 12345",
  "LegalForm.SupervisoryBoardChair": "Supervisory board chair",
  "LegalForm.SoleProprietorship": "Sole proprietorship",
  "LegalForm.Gbr": "GbR",
  "LegalForm.Egbr": "eGbR",
  "LegalForm.RegisteredMerchant": "Registered merchant",
  "LegalForm.Gmbh": "GmbH",
  "LegalForm.Ug": "UG (limited liability)",
  "LegalForm.Ag": "AG",
  "LegalForm.Ohg": "OHG",
  "LegalForm.Kg": "KG",
  "LegalForm.GmbhCoKg": "GmbH & Co. KG",
  "LegalForm.Ggmbh": "gGmbH",
  "LegalForm.Se": "SE",
  "LegalForm.Ev": "Registered association (e.V.)",
  "LegalForm.RepresentativesOwner": "Owner",
  "LegalForm.RepresentativesPartners": "Partners",
  "LegalForm.RepresentativesManagingDirectors": "Managing directors",
  "LegalForm.RepresentativesManagementBoard": "Management board",
  "LegalForm.RepresentativesGeneralPartners": "General partners",
  "LegalForm.RepresentativesGeneralPartnerCompany": "General partner",
  "LegalForm.CompanyNameHint": "Full company name including legal form",
  "LegalForm.NameMismatch": "The suffix in the company name does not match the selected legal form.",
  "LegalForm.UgSuffixIncomplete": "A UG must carry the suffix “(haftungsbeschränkt)” in its company name.",
} as const;

export const sharedLegalFormRu = {
  "LegalForm.Label": "Правовая форма",
  "LegalForm.RegisterCourt": "Регистрационный суд",
  "LegalForm.RegisterNumber": "Регистрационный номер",
  "LegalForm.RegisterNumberPlaceholder": "напр. HRB 12345",
  "LegalForm.SupervisoryBoardChair": "Председатель наблюдательного совета",
  "LegalForm.SoleProprietorship": "Индивидуальное предприятие",
  "LegalForm.Gbr": "GbR",
  "LegalForm.Egbr": "eGbR",
  "LegalForm.RegisteredMerchant": "Зарегистрированный коммерсант",
  "LegalForm.Gmbh": "GmbH",
  "LegalForm.Ug": "UG (с ограниченной ответственностью)",
  "LegalForm.Ag": "AG",
  "LegalForm.Ohg": "OHG",
  "LegalForm.Kg": "KG",
  "LegalForm.GmbhCoKg": "GmbH & Co. KG",
  "LegalForm.Ggmbh": "gGmbH",
  "LegalForm.Se": "SE",
  "LegalForm.Ev": "Зарегистрированное объединение (e.V.)",
  "LegalForm.RepresentativesOwner": "Владелец",
  "LegalForm.RepresentativesPartners": "Участники",
  "LegalForm.RepresentativesManagingDirectors": "Управляющие директора",
  "LegalForm.RepresentativesManagementBoard": "Правление",
  "LegalForm.RepresentativesGeneralPartners": "Полные товарищи",
  "LegalForm.RepresentativesGeneralPartnerCompany": "Полный товарищ",
  "LegalForm.CompanyNameHint": "Полное наименование с указанием правовой формы",
  "LegalForm.NameMismatch": "Указание в названии фирмы не соответствует выбранной правовой форме.",
  "LegalForm.UgSuffixIncomplete": "UG должна указывать в названии «(haftungsbeschränkt)».",
} as const;

export const sharedLegalFormUk = {
  "LegalForm.Label": "Правова форма",
  "LegalForm.RegisterCourt": "Реєстраційний суд",
  "LegalForm.RegisterNumber": "Реєстраційний номер",
  "LegalForm.RegisterNumberPlaceholder": "напр. HRB 12345",
  "LegalForm.SupervisoryBoardChair": "Голова наглядової ради",
  "LegalForm.SoleProprietorship": "Індивідуальне підприємство",
  "LegalForm.Gbr": "GbR",
  "LegalForm.Egbr": "eGbR",
  "LegalForm.RegisteredMerchant": "Зареєстрований комерсант",
  "LegalForm.Gmbh": "GmbH",
  "LegalForm.Ug": "UG (з обмеженою відповідальністю)",
  "LegalForm.Ag": "AG",
  "LegalForm.Ohg": "OHG",
  "LegalForm.Kg": "KG",
  "LegalForm.GmbhCoKg": "GmbH & Co. KG",
  "LegalForm.Ggmbh": "gGmbH",
  "LegalForm.Se": "SE",
  "LegalForm.Ev": "Зареєстроване об’єднання (e.V.)",
  "LegalForm.RepresentativesOwner": "Власник",
  "LegalForm.RepresentativesPartners": "Учасники",
  "LegalForm.RepresentativesManagingDirectors": "Керуючі директори",
  "LegalForm.RepresentativesManagementBoard": "Правління",
  "LegalForm.RepresentativesGeneralPartners": "Повні товариші",
  "LegalForm.RepresentativesGeneralPartnerCompany": "Повний товариш",
  "LegalForm.CompanyNameHint": "Повна назва з правовою формою",
  "LegalForm.NameMismatch": "Позначка в назві фірми не відповідає обраній правовій формі.",
  "LegalForm.UgSuffixIncomplete": "UG має містити в назві «(haftungsbeschränkt)».",
} as const;

export const sharedLegalFormIt = {
  "LegalForm.Label": "Forma giuridica",
  "LegalForm.RegisterCourt": "Tribunale del registro",
  "LegalForm.RegisterNumber": "Numero di registro",
  "LegalForm.RegisterNumberPlaceholder": "es. HRB 12345",
  "LegalForm.SupervisoryBoardChair": "Presidenza del consiglio di sorveglianza",
  "LegalForm.SoleProprietorship": "Impresa individuale",
  "LegalForm.Gbr": "GbR",
  "LegalForm.Egbr": "eGbR",
  "LegalForm.RegisteredMerchant": "Commerciante registrato",
  "LegalForm.Gmbh": "GmbH",
  "LegalForm.Ug": "UG (responsabilità limitata)",
  "LegalForm.Ag": "AG",
  "LegalForm.Ohg": "OHG",
  "LegalForm.Kg": "KG",
  "LegalForm.GmbhCoKg": "GmbH & Co. KG",
  "LegalForm.Ggmbh": "gGmbH",
  "LegalForm.Se": "SE",
  "LegalForm.Ev": "Associazione registrata (e.V.)",
  "LegalForm.RepresentativesOwner": "Titolare",
  "LegalForm.RepresentativesPartners": "Soci",
  "LegalForm.RepresentativesManagingDirectors": "Amministratori delegati",
  "LegalForm.RepresentativesManagementBoard": "Consiglio di gestione",
  "LegalForm.RepresentativesGeneralPartners": "Soci accomandatari",
  "LegalForm.RepresentativesGeneralPartnerCompany": "Socio accomandatario",
  "LegalForm.CompanyNameHint": "Ragione sociale completa con forma giuridica",
  "LegalForm.NameMismatch": "Il suffisso nella ragione sociale non corrisponde alla forma giuridica scelta.",
  "LegalForm.UgSuffixIncomplete": "Una UG deve riportare «(haftungsbeschränkt)» nella ragione sociale.",
} as const;

export const sharedLegalFormTranslations = {
  de: sharedLegalFormDe,
  en: sharedLegalFormEn,
  ru: sharedLegalFormRu,
  uk: sharedLegalFormUk,
  it: sharedLegalFormIt,
} as const;

export type SharedLegalFormLang = keyof typeof sharedLegalFormTranslations;
export type SharedLegalFormKey = keyof typeof sharedLegalFormDe;
